import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link as RouterLink, useParams } from '@tanstack/react-router';
import {
  AnnotationType,
  type Annotation,
  type CreateLabelInput,
  type ExportFormat,
  type ExtractedImage,
  type ExtractedImageUpload,
  type Label,
  type ShareLink,
  type NormalizedBox,
  type SourcePlaybackResponse,
  type Video,
} from '@ddp/shared';
import Button from '@carbon/react/es/components/Button/Button';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import TextArea from '@carbon/react/es/components/TextArea/TextArea';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import Modal from '@carbon/react/es/components/Modal/Modal';
import Loading from '@carbon/react/es/components/Loading/Loading';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';

import { apiDownload, apiFetch } from '../api/client.js';
import { track } from '../features/analytics/analytics.js';
import { useFramePreview } from '../features/video/use-frame-preview.js';
import { useAnnotationVideo } from '../features/video/use-annotation-video.js';

/** Pas d'avance image par image, à défaut de fréquence connue (la source n'est pas encore sondée). */
const DEFAULT_FRAME_STEP_S = 1 / 30;
/** En deçà, un cliquer-glisser est traité comme un clic, pas comme une boîte. */
const MIN_BOX_SIZE = 0.01;

interface Point {
  x: number;
  y: number;
}

/**
 * Image extraite de la vidéo, gardée **en mémoire** avec sa référence temporelle. On annote sur
 * cette image fixe plutôt que sur la vidéo en lecture : le tracé y est stable et fiable (pas de
 * frame qui bouge, pas de conflit entre le glissement du doigt et le défilement de la page), et
 * cela correspond au geste attendu — « extraire une image puis l'annoter ».
 */
interface ExtractedFrame {
  /** Identifiant d'affichage : id serveur si persistée, sinon un id local temporaire. */
  id: string;
  /** Id serveur, présent une fois le dépôt confirmé. `undefined` tant que la persistance court. */
  serverId?: string;
  timestampMs: number;
  /** Source affichable **plein format** : data-URL locale (fraîche) ou URL pré-signée (rechargée). */
  dataUrl: string;
  /** Vignette légère (JPEG réduit) pour la bande latérale ; absente → repli sur `dataUrl`. */
  thumbUrl?: string;
  createdByName?: string | null;
  createdAt?: Date;
}

export function VideoAnnotatePage() {
  const { videoId } = useParams({ strict: false }) as { videoId: string };

  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const annotationsRef = useRef<Annotation[]>([]);
  const draftRef = useRef<{ start: Point; current: Point } | null>(null);
  const selectedColorRef = useRef<string>('#0f62fe');
  const frameCounter = useRef(0);

  const [video, setVideo] = useState<Video | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [labels, setLabels] = useState<Label[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [frames, setFrames] = useState<ExtractedFrame[]>([]);
  const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null);
  const [selectedLabelId, setSelectedLabelId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#0f62fe');
  // Boîte survolée dans la liste : mise en évidence sur l'image pour identifier laquelle
  // supprimer (toutes portent le même label/instant, donc indistinguables autrement).
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const highlightedIdRef = useRef<string | null>(null);
  highlightedIdRef.current = highlightedId;

  // Gestion : filtre par label (''=tous), annotation en cours d'édition, état de l'export.
  const [filterLabelId, setFilterLabelId] = useState<string>('');
  const [editing, setEditing] = useState<Annotation | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState<ExportFormat | null>(null);
  // Largeur de la colonne vidéo (en %), réglable en desktop pour mieux voir les objets. Au-delà
  // d'un certain seuil, le panneau d'annotations ne tient plus à côté et passe dessous (flex-wrap).
  const [videoBasis, setVideoBasis] = useState(60);
  // Seuil de confiance des suggestions du modèle (incrément 2) : masque à l'écran et borne
  // l'« accepter tout » les prédictions moins sûres que ce curseur.
  const [suggestionThreshold, setSuggestionThreshold] = useState(0.25);
  const suggestionThresholdRef = useRef(suggestionThreshold);
  suggestionThresholdRef.current = suggestionThreshold;
  const [reviewBusy, setReviewBusy] = useState(false);

  // Source lisible par le navigateur : bascule sur le HLS H.264 si la source d'origine n'est pas
  // décodable ici (H.265 hors Safari), sinon `pending` le temps du transcodage (voir le hook).
  const { mode: videoMode, posterUrl } = useAnnotationVideo(
    videoRef,
    videoId,
    sourceUrl,
    video?.status,
  );

  // Aperçu échantillonné : vignettes de survol (scrubbing) et poster de repli. On ne l'échantillonne
  // que sur la source d'origine ; en repli HLS, l'octet brut n'est de toute façon pas décodable ici.
  const { poster, frames: previewFrames } = useFramePreview(videoMode === 'raw' ? sourceUrl : null);
  const [hoverPreview, setHoverPreview] = useState<string | null>(null);

  // La vignette de survol est une aide au scrubbing **à l'arrêt**. Dès que la lecture démarre, on la
  // retire : l'utilisateur peut lancer la lecture sans bouger la souris, donc `onMouseMove` ne suffit
  // pas — sans cet écouteur, une vignette figée resterait au-dessus de la vidéo qui joue.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const clear = () => setHoverPreview(null);
    video.addEventListener('play', clear);
    video.addEventListener('playing', clear);
    return () => {
      video.removeEventListener('play', clear);
      video.removeEventListener('playing', clear);
    };
  }, [sourceUrl]);

  // Menu contextuel (clic droit) sur l'image extraite : télécharger / partager / éditer / supprimer.
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  /** Crée un lien de partage public pour l'image extraite persistée et copie l'URL. */
  const onShareImage = async () => {
    const frame = selectedFrameRef.current;
    if (!frame?.serverId) {
      setActionError('Image pas encore enregistrée — réessayez dans un instant.');
      return;
    }
    setActionError(null);
    try {
      const link = await apiFetch<ShareLink>('/shares', {
        method: 'POST',
        body: { scope: 'IMAGE', resourceId: frame.serverId },
      });
      setShareUrl(link.url);
      try {
        await navigator.clipboard.writeText(link.url);
      } catch {
        // Presse-papiers refusé : l'URL reste affichée et sélectionnable.
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Partage impossible');
    }
  };
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu]);

  const onPlayerHover = (event: React.MouseEvent<HTMLDivElement>) => {
    if (previewFrames.length === 0) return;
    const video = videoRef.current;
    // Pendant la lecture, on n'obscurcit pas l'image avec la vignette.
    if (video && !video.paused) {
      setHoverPreview(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(0.999, Math.max(0, (event.clientX - rect.left) / rect.width));
    const index = Math.floor(ratio * previewFrames.length);
    setHoverPreview(previewFrames[index]?.dataUrl ?? null);
  };

  const selectedFrame = frames.find((f) => f.id === selectedFrameId) ?? null;
  const selectedFrameRef = useRef<ExtractedFrame | null>(null);
  selectedFrameRef.current = selectedFrame;
  annotationsRef.current = annotations;
  selectedColorRef.current = labels.find((l) => l.id === selectedLabelId)?.color ?? '#0f62fe';

  // Annotations « réelles » : humaines ou suggestions validées. Les suggestions du modèle non
  // encore revues (`MODEL`) ne comptent pas dans le jeu de données ni dans la liste globale — elles
  // vivent dans leur propre panneau (incrément 2, ADR-0013).
  const reviewedAnnotations = annotations.filter((a) => a.source !== 'MODEL');

  // Boîtes posées sur l'image actuellement affichée (même instant que la frame).
  const frameBoxes = selectedFrame
    ? reviewedAnnotations.filter((a) => a.box && a.startMs === selectedFrame.timestampMs)
    : [];
  // Suggestions du modèle sur cette frame, au-dessus du seuil, triées par confiance décroissante.
  const frameSuggestions = selectedFrame
    ? annotations
        .filter(
          (a) =>
            a.source === 'MODEL' &&
            a.box &&
            a.startMs === selectedFrame.timestampMs &&
            (a.confidence ?? 0) >= suggestionThreshold,
        )
        .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
    : [];
  // Toutes les suggestions de la frame (seuil ignoré) : sert à décider d'afficher le panneau et à
  // signaler combien le curseur en masque.
  const frameSuggestionTotal = selectedFrame
    ? annotations.filter(
        (a) => a.source === 'MODEL' && a.box && a.startMs === selectedFrame.timestampMs,
      ).length
    : 0;

  // Le filtre par label s'applique à la liste globale et aux compteurs, pas au dessin sur l'image.
  const visibleAnnotations = filterLabelId
    ? reviewedAnnotations.filter((a) => a.labelId === filterLabelId)
    : reviewedAnnotations;

  // Vue d'ensemble : décompte par label (couleur incluse) pour piloter le jeu de données.
  const labelCounts = labels
    .map((label) => ({
      label,
      count: reviewedAnnotations.filter((a) => a.labelId === label.id).length,
    }))
    .filter((entry) => entry.count > 0);
  const boxCount = reviewedAnnotations.filter((a) => a.box).length;

  const canDraw = Boolean(selectedFrame) && Boolean(selectedLabelId);

  const refreshAnnotations = useCallback(async () => {
    const list = await apiFetch<Annotation[]>(`/videos/${videoId}/annotations`);
    setAnnotations(list);
  }, [videoId]);

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [videoData, source, labelList, annotationList, imageList] = await Promise.all([
          apiFetch<Video>(`/videos/${videoId}`),
          apiFetch<SourcePlaybackResponse>(`/videos/${videoId}/source`),
          apiFetch<Label[]>(`/labels`),
          apiFetch<Annotation[]>(`/videos/${videoId}/annotations`),
          apiFetch<ExtractedImage[]>(`/videos/${videoId}/images`),
        ]);
        if (!active) return;
        setVideo(videoData);
        setSourceUrl(source.url);
        setLabels(labelList);
        setAnnotations(annotationList);
        // Images déjà extraites : rechargées comme frames annotables (source = URL pré-signée).
        setFrames(
          imageList.map((img) => ({
            id: img.id,
            serverId: img.id,
            timestampMs: img.timestampMs,
            dataUrl: img.url,
            thumbUrl: img.thumbUrl ?? undefined,
            createdByName: img.createdByName,
            createdAt: img.createdAt,
          })),
        );
        setSelectedLabelId((prev) => prev || labelList[0]?.id || '');
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Chargement impossible');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [videoId]);

  /** Redessine le calque de l'image active : boîtes de cette frame + rectangle en cours de tracé. */
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;
    const width = image.clientWidth;
    const height = image.clientHeight;
    if (width === 0 || height === 0) return;

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const frame = selectedFrameRef.current;
    if (frame) {
      // Une frame extraite porte un instant exact : on affiche les boîtes posées à ce même instant.
      for (const annotation of annotationsRef.current) {
        if (!annotation.box || annotation.startMs !== frame.timestampMs) continue;
        const isSuggestion = annotation.source === 'MODEL';
        // Une suggestion sous le seuil de confiance est masquée (curseur de l'UI).
        if (isSuggestion && (annotation.confidence ?? 0) < suggestionThresholdRef.current) continue;
        const emphasized = annotation.id === highlightedIdRef.current;
        const labelText = isSuggestion
          ? `${annotation.labelName} ${Math.round((annotation.confidence ?? 0) * 100)}%`
          : annotation.labelName;
        drawBox(
          ctx,
          annotation.box,
          width,
          height,
          annotation.labelColor,
          labelText,
          emphasized,
          isSuggestion
            ? { dashed: true, alpha: confidenceAlpha(annotation.confidence) }
            : undefined,
        );
      }
    }

    const draft = draftRef.current;
    if (draft) {
      const box = toBox(draft.start, draft.current);
      ctx.setLineDash([6, 4]);
      drawBox(ctx, box, width, height, selectedColorRef.current, null, false);
      ctx.setLineDash([]);
    }
  }, []);

  // Redessine quand l'image active change ou que la fenêtre est redimensionnée.
  useEffect(() => {
    const image = imageRef.current;
    if (!image) return;
    const observer = new ResizeObserver(redraw);
    observer.observe(image);
    return () => observer.disconnect();
  }, [redraw, selectedFrameId]);

  useEffect(() => {
    redraw();
  }, [annotations, selectedFrameId, highlightedId, suggestionThreshold, redraw]);

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: clamp01((event.clientX - rect.left) / rect.width),
      y: clamp01((event.clientY - rect.top) / rect.height),
    };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!canDraw) return;
    const point = pointFromEvent(event);
    draftRef.current = { start: point, current: point };
    event.currentTarget.setPointerCapture(event.pointerId);
    redraw();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draftRef.current) return;
    draftRef.current = { ...draftRef.current, current: pointFromEvent(event) };
    redraw();
  };

  const onPointerUp = async () => {
    const draft = draftRef.current;
    draftRef.current = null;
    const frame = selectedFrameRef.current;
    if (!draft || !frame) return;
    const box = toBox(draft.start, draft.current);
    redraw();
    // Un rectangle minuscule est un clic accidentel, pas une annotation.
    if (box.width < MIN_BOX_SIZE || box.height < MIN_BOX_SIZE) return;
    if (!selectedLabelId) return;

    setActionError(null);
    try {
      await apiFetch(`/videos/${videoId}/annotations`, {
        method: 'POST',
        body: {
          type: AnnotationType.BBOX,
          labelId: selectedLabelId,
          startMs: frame.timestampMs,
          box,
        },
      });
      track('ANNOTATION_CREATED', { type: 'bbox' });
      await refreshAnnotations();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Enregistrement de la boîte impossible');
    }
  };

  /** Capture la frame courante de la vidéo en image fixe, gardée en mémoire et sélectionnée. */
  const extractFrame = () => {
    const videoEl = videoRef.current;
    if (!videoEl || !videoEl.videoWidth) return;
    setActionError(null);
    const canvas = document.createElement('canvas');
    canvas.width = videoEl.videoWidth;
    canvas.height = videoEl.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    try {
      ctx.drawImage(videoEl, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');
      const timestampMs = Math.round(videoEl.currentTime * 1000);
      frameCounter.current += 1;
      // Id local temporaire : l'affichage est immédiat, la persistance suit en arrière-plan.
      const localId = `f${frameCounter.current}`;
      const frame: ExtractedFrame = { id: localId, timestampMs, dataUrl };
      setFrames((prev) => [frame, ...prev]);
      setSelectedFrameId(localId);
      canvas.toBlob((blob) => {
        if (!blob) return;
        // Vignette légère pour la bande latérale : le PNG plein format peut peser plusieurs Mo, la
        // vignette quelques dizaines de Ko. Dépôt au mieux — un échec n'empêche pas l'enregistrement.
        thumbnailBlob(canvas).then((thumb) =>
          persistFrame(localId, timestampMs, canvas.width, canvas.height, blob, thumb),
        );
      }, 'image/png');
    } catch {
      // Canevas « teinté » : le stockage n'a pas renvoyé les en-têtes CORS attendus.
      setActionError('Extraction d’image impossible (CORS du stockage).');
    }
  };

  /**
   * Persiste une image extraite : l'octet part droit vers le stockage via une URL pré-signée
   * (ADR-0010), l'API ne fait qu'encadrer. Le lien local est promu en image serveur à la
   * confirmation, sans re-télécharger l'aperçu déjà affiché.
   */
  const persistFrame = async (
    localId: string,
    timestampMs: number,
    width: number,
    height: number,
    blob: Blob,
    thumb: Blob | null,
  ) => {
    try {
      const upload = await apiFetch<ExtractedImageUpload>(`/videos/${videoId}/images`, {
        method: 'POST',
        body: { timestampMs, width, height },
      });
      // Dépôt direct : requête pré-signée, sans jeton ni cookie (ils casseraient la signature).
      const put = await fetch(upload.uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': 'image/png' },
      });
      if (!put.ok) throw new Error('Dépôt de l’image refusé par le stockage');
      // Vignette au mieux : son échec ne compromet pas l'image (l'aperçu retombera sur le PNG).
      let hasThumbnail = false;
      if (thumb) {
        const putThumb = await fetch(upload.thumbUploadUrl, {
          method: 'PUT',
          body: thumb,
          headers: { 'Content-Type': 'image/jpeg' },
        }).catch(() => null);
        hasThumbnail = Boolean(putThumb?.ok);
      }
      await apiFetch(`/videos/${videoId}/images/${upload.id}/confirm`, {
        method: 'POST',
        body: { hasThumbnail },
      });
      setFrames((prev) => prev.map((f) => (f.id === localId ? { ...f, serverId: upload.id } : f)));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Enregistrement de l’image impossible');
    }
  };

  /**
   * Télécharge l'image affichée, avec ou sans ses annotations.
   *
   * On repasse toujours par un canvas : l'attribut `download` d'un `<a>` est ignoré pour une URL
   * pré-signée (autre origine), ce qui faisait « ne rien se passer ». Le rendu canvas → blob
   * fonctionne quelle que soit la source (data-URL fraîche ou URL signée rechargée).
   */
  const downloadFrame = async (withAnnotations: boolean) => {
    const frame = selectedFrameRef.current;
    if (!frame) return;
    setActionError(null);
    try {
      const boxes = withAnnotations
        ? annotationsRef.current.filter(
            // On ne grave pas les suggestions non validées sur l'image téléchargée.
            (a) => a.box && a.startMs === frame.timestampMs && a.source !== 'MODEL',
          )
        : [];
      const blob = await renderFrameBlob(frame.dataUrl, boxes);
      const suffix = withAnnotations ? 'annotee' : 'image';
      triggerBlobDownload(blob, `${video?.title ?? 'frame'}-${frame.timestampMs}ms-${suffix}.png`);
    } catch {
      setActionError('Téléchargement de l’image impossible (CORS du stockage).');
    }
  };

  const removeFrame = async (frame: ExtractedFrame) => {
    // Image persistée : suppression logique côté serveur avant de la retirer de l'affichage.
    if (frame.serverId) {
      try {
        await apiFetch(`/videos/${videoId}/images/${frame.serverId}`, { method: 'DELETE' });
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Suppression de l’image impossible');
        return;
      }
    }
    setFrames((prev) => prev.filter((f) => f.id !== frame.id));
    setSelectedFrameId((current) => (current === frame.id ? null : current));
  };

  const onCreateLabel = async () => {
    const name = newLabelName.trim();
    if (!name) return;
    setActionError(null);
    try {
      const label = await apiFetch<Label>('/labels', {
        method: 'POST',
        body: { name, color: newLabelColor } satisfies CreateLabelInput,
      });
      setLabels((prev) => [...prev, label].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedLabelId(label.id);
      setNewLabelName('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Création du label impossible');
    }
  };

  const onDeleteAnnotation = async (annotationId: string) => {
    setActionError(null);
    try {
      await apiFetch(`/videos/${videoId}/annotations/${annotationId}`, { method: 'DELETE' });
      await refreshAnnotations();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Suppression impossible');
    }
  };

  /** Valide une suggestion du modèle : elle passe en `HUMAN_VERIFIED` et rejoint le jeu de données. */
  const onAcceptSuggestion = async (annotationId: string) => {
    setActionError(null);
    setReviewBusy(true);
    try {
      await apiFetch(`/videos/${videoId}/annotations/${annotationId}/accept`, {
        method: 'POST',
        body: {},
      });
      await refreshAnnotations();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Validation impossible');
    } finally {
      setReviewBusy(false);
    }
  };

  /** Rejette une suggestion : suppression logique (même chemin qu'une annotation supprimée). */
  const onRejectSuggestion = (annotationId: string) => onDeleteAnnotation(annotationId);

  /** Valide d'un coup toutes les suggestions de la frame au-dessus du seuil de confiance courant. */
  const onAcceptAllSuggestions = async () => {
    const frame = selectedFrameRef.current;
    if (!frame) return;
    setActionError(null);
    setReviewBusy(true);
    try {
      await apiFetch(`/videos/${videoId}/annotations/accept`, {
        method: 'POST',
        body: { minConfidence: suggestionThreshold, startMs: frame.timestampMs },
      });
      await refreshAnnotations();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Validation en lot impossible');
    } finally {
      setReviewBusy(false);
    }
  };

  const onExport = async (format: ExportFormat) => {
    setExportError(null);
    setExportBusy(format);
    try {
      const name = `annotations-${video?.title ?? videoId}`;
      const ext = format === 'coco' ? 'coco.json' : format;
      await apiDownload(`/videos/${videoId}/annotations/export?format=${format}`, `${name}.${ext}`);
    } catch (err) {
      // COCO renvoie 409 tant que la résolution n'est pas connue : on affiche le message de l'API.
      setExportError(err instanceof Error ? err.message : 'Export impossible');
    } finally {
      setExportBusy(null);
    }
  };

  const onSaveEdit = async (annotationId: string, labelId: string, note: string) => {
    const target = annotationsRef.current.find((a) => a.id === annotationId);
    if (target?.source === 'MODEL') {
      // Corriger une suggestion = la valider avec le bon label (l'erreur de classe est la plus
      // fréquente du modèle générique). La note n'appartient pas au flux de validation.
      await apiFetch(`/videos/${videoId}/annotations/${annotationId}/accept`, {
        method: 'POST',
        body: { labelId },
      });
    } else {
      await apiFetch(`/videos/${videoId}/annotations/${annotationId}`, {
        method: 'PATCH',
        body: { labelId, note: note.trim() === '' ? null : note.trim() },
      });
    }
    setEditing(null);
    await refreshAnnotations();
  };

  const stepFrame = (direction: 1 | -1) => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    videoEl.pause();
    videoEl.currentTime = Math.max(0, videoEl.currentTime + direction * DEFAULT_FRAME_STEP_S);
  };

  /** Compte les boîtes déjà posées sur une frame donnée. */
  const boxCountAt = (timestampMs: number) =>
    annotations.filter((a) => a.box && a.startMs === timestampMs).length;

  if (loading) {
    return (
      <section className="ddp-page__section">
        <Loading withOverlay={false} description="Chargement…" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="ddp-page__section">
        <InlineNotification
          kind="error"
          title="Erreur"
          subtitle={error}
          lowContrast
          hideCloseButton
        />
        <p>
          <RouterLink to="/data">← Retour aux vidéos</RouterLink>
        </p>
      </section>
    );
  }

  return (
    <section className="ddp-page__section ddp-annotate">
      <header className="ddp-annotate__header">
        <RouterLink to="/data">← Vidéos</RouterLink>
        <h1>{video?.title}</h1>
        {video && (
          <p className="ddp-annotate__meta">
            Ajoutée le {formatDate(video.createdAt)}
            {video.uploadedByName ? ` par ${video.uploadedByName}` : ''}
          </p>
        )}
      </header>

      <div
        className="ddp-annotate__layout"
        style={{ '--ddp-video-basis': `${videoBasis}%` } as CSSProperties}
      >
        <div className="ddp-annotate__main">
          {/* La vidéo sert à naviguer et à extraire des images ; on n'annote plus dessus. */}
          <div
            className="ddp-player"
            onMouseMove={onPlayerHover}
            onMouseLeave={() => setHoverPreview(null)}
          >
            {sourceUrl && (
              // `src` est posé par `useAnnotationVideo` (source d'origine, ou HLS en repli) — pas ici.
              <video
                ref={videoRef}
                className="ddp-player__video"
                poster={posterUrl ?? poster ?? undefined}
                crossOrigin="anonymous"
                controls
                preload="metadata"
              />
            )}
            {videoMode === 'pending' && (
              <div className="ddp-player__notice" role="status">
                Ce navigateur ne peut pas lire le format de la source directement. La conversion en
                un format standard est en cours — l’annotation sera disponible dès que la vidéo sera
                prête (ou ouvrez-la dans Safari).
              </div>
            )}
            {/* Vignette de survol : image clé la plus proche de la position du curseur. */}
            {hoverPreview && (
              <img src={hoverPreview} alt="" className="ddp-player__scrub" aria-hidden="true" />
            )}
          </div>

          <div className="ddp-annotate__toolbar">
            <Button kind="tertiary" size="sm" onClick={() => stepFrame(-1)}>
              ⟨ Image préc.
            </Button>
            <Button kind="tertiary" size="sm" onClick={() => stepFrame(1)}>
              Image suiv. ⟩
            </Button>
            <Button kind="primary" size="sm" onClick={extractFrame}>
              Extraire l’image
            </Button>
            {/* Réglage de la taille de la vidéo (desktop). */}
            <label className="ddp-annotate__resize">
              <span>Taille</span>
              <input
                type="range"
                min={40}
                max={95}
                step={5}
                value={videoBasis}
                onChange={(e) => setVideoBasis(Number(e.target.value))}
                aria-label="Taille de la vidéo"
              />
            </label>
          </div>
          <p className="ddp-annotate__hint">
            Placez-vous sur une frame (lecture, ou image par image), puis « Extraire l’image » pour
            l’annoter ci-dessous.
          </p>

          {/* Surface d'annotation : l'image extraite sélectionnée + calque de dessin. */}
          {selectedFrame ? (
            <div className="ddp-annotate__surface">
              {/* Clic droit : menu (télécharger avec/sans annotation, éditer, supprimer). */}
              <div
                className="ddp-frame"
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({ x: e.clientX, y: e.clientY });
                }}
              >
                <img
                  ref={imageRef}
                  className="ddp-frame__img"
                  src={selectedFrame.dataUrl}
                  alt={`Frame à ${formatMs(selectedFrame.timestampMs)}`}
                  onLoad={redraw}
                />
                <canvas
                  ref={canvasRef}
                  className={`ddp-frame__canvas${canDraw ? ' ddp-frame__canvas--active' : ''}`}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={() => void onPointerUp()}
                />
              </div>
              <div className="ddp-annotate__surface-meta">
                <span>Image à {formatMs(selectedFrame.timestampMs)}</span>
                <Button kind="ghost" size="sm" onClick={() => void downloadFrame(false)}>
                  Télécharger
                </Button>
                <Button kind="ghost" size="sm" onClick={() => void downloadFrame(true)}>
                  Télécharger annotée
                </Button>
              </div>
              <p className="ddp-annotate__hint">
                {canDraw
                  ? 'Dessinez un rectangle sur l’image pour l’annoter.'
                  : 'Choisissez un label pour dessiner une boîte.'}
              </p>

              {frameBoxes.length > 0 && (
                <ul className="ddp-annotate__boxes">
                  {frameBoxes.map((annotation, index) => (
                    <li
                      key={annotation.id}
                      className="ddp-annotate__box"
                      onMouseEnter={() => setHighlightedId(annotation.id)}
                      onMouseLeave={() =>
                        setHighlightedId((current) => (current === annotation.id ? null : current))
                      }
                    >
                      <span
                        className="ddp-annotate__swatch"
                        style={{ backgroundColor: annotation.labelColor }}
                      />
                      <span className="ddp-annotate__box-label">
                        {annotation.labelName} · boîte {index + 1}
                      </span>
                      <Button
                        kind="danger--ghost"
                        size="sm"
                        onClick={() => void onDeleteAnnotation(annotation.id)}
                      >
                        Supprimer
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Suggestions du modèle (ADR-0013, incrément 2) : un modèle propose, l'humain valide. */}
              {frameSuggestionTotal > 0 && (
                <div className="ddp-suggest">
                  <div className="ddp-suggest__head">
                    <h3 className="ddp-suggest__title">
                      Suggestions du modèle ({frameSuggestions.length}
                      {frameSuggestions.length !== frameSuggestionTotal
                        ? ` / ${frameSuggestionTotal}`
                        : ''}
                      )
                    </h3>
                    <label className="ddp-suggest__threshold">
                      <span>Confiance ≥ {Math.round(suggestionThreshold * 100)}%</span>
                      <input
                        type="range"
                        min={0}
                        max={95}
                        step={5}
                        value={Math.round(suggestionThreshold * 100)}
                        onChange={(e) => setSuggestionThreshold(Number(e.target.value) / 100)}
                        aria-label="Seuil de confiance des suggestions"
                      />
                    </label>
                  </div>

                  {frameSuggestions.length === 0 ? (
                    <p className="ddp-annotate__hint">
                      Toutes les suggestions sont sous le seuil. Baissez la confiance pour les
                      revoir.
                    </p>
                  ) : (
                    <>
                      <ul className="ddp-suggest__list">
                        {frameSuggestions.map((suggestion) => (
                          <li
                            key={suggestion.id}
                            className="ddp-suggest__item"
                            onMouseEnter={() => setHighlightedId(suggestion.id)}
                            onMouseLeave={() =>
                              setHighlightedId((current) =>
                                current === suggestion.id ? null : current,
                              )
                            }
                          >
                            <span
                              className="ddp-annotate__swatch"
                              style={{ backgroundColor: suggestion.labelColor }}
                            />
                            <span className="ddp-suggest__label">{suggestion.labelName}</span>
                            <span
                              className="ddp-suggest__conf"
                              title={`Confiance du modèle ${suggestion.modelName ?? ''}`}
                            >
                              {Math.round((suggestion.confidence ?? 0) * 100)}%
                            </span>
                            <span className="ddp-suggest__actions">
                              <Button
                                kind="primary"
                                size="sm"
                                disabled={reviewBusy}
                                onClick={() => void onAcceptSuggestion(suggestion.id)}
                              >
                                Accepter
                              </Button>
                              <Button
                                kind="tertiary"
                                size="sm"
                                disabled={reviewBusy}
                                onClick={() => setEditing(suggestion)}
                              >
                                Corriger
                              </Button>
                              <Button
                                kind="danger--ghost"
                                size="sm"
                                disabled={reviewBusy}
                                onClick={() => void onRejectSuggestion(suggestion.id)}
                              >
                                Rejeter
                              </Button>
                            </span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        kind="primary"
                        size="sm"
                        disabled={reviewBusy}
                        onClick={() => void onAcceptAllSuggestions()}
                      >
                        Accepter tout ({frameSuggestions.length}) ≥{' '}
                        {Math.round(suggestionThreshold * 100)}%
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="ddp-annotate__empty">
              Aucune image extraite. Utilisez « Extraire l’image » pour en capturer une et
              l’annoter.
            </p>
          )}
        </div>

        <aside className="ddp-annotate__side">
          {actionError && (
            <InlineNotification
              kind="error"
              title="Action impossible"
              subtitle={actionError}
              lowContrast
              hideCloseButton
            />
          )}
          {shareUrl && (
            <InlineNotification
              kind="success"
              title="Lien de partage copié"
              subtitle={shareUrl}
              lowContrast
              onCloseButtonClick={() => setShareUrl(null)}
            />
          )}

          <Select
            id="label"
            labelText="Label actif"
            value={selectedLabelId}
            onChange={(e) => setSelectedLabelId(e.target.value)}
          >
            {labels.length === 0 && <SelectItem value="" text="Aucun label — créez-en un" />}
            {labels.map((label) => (
              <SelectItem key={label.id} value={label.id} text={label.name} />
            ))}
          </Select>

          <div className="ddp-annotate__newlabel">
            <TextInput
              id="new-label"
              labelText="Nouveau label"
              placeholder="ex. Véhicule"
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
            />
            <input
              aria-label="Couleur du label"
              type="color"
              className="ddp-annotate__color"
              value={newLabelColor}
              onChange={(e) => setNewLabelColor(e.target.value)}
            />
            <Button kind="tertiary" size="sm" onClick={() => void onCreateLabel()}>
              Ajouter
            </Button>
          </div>

          {/* Vue d'ensemble : total, décompte par type et par label, pour piloter le jeu de données. */}
          <h2 className="ddp-annotate__subtitle">Vue d’ensemble</h2>
          <dl className="ddp-annotate__overview">
            <div>
              <dt>Total</dt>
              <dd>{annotations.length}</dd>
            </div>
            <div>
              <dt>Boîtes</dt>
              <dd>{boxCount}</dd>
            </div>
          </dl>
          {labelCounts.length > 0 && (
            <ul className="ddp-annotate__legend">
              {labelCounts.map(({ label, count }) => (
                <li key={label.id} className="ddp-annotate__legend-item" title={labelOrigin(label)}>
                  <span className="ddp-annotate__swatch" style={{ backgroundColor: label.color }} />
                  <span className="ddp-annotate__legend-name">{label.name}</span>
                  <span className="ddp-annotate__legend-count">{count}</span>
                </li>
              ))}
            </ul>
          )}

          <h2 className="ddp-annotate__subtitle">Export</h2>
          {exportError && (
            <InlineNotification
              kind="error"
              title="Export impossible"
              subtitle={exportError}
              lowContrast
              hideCloseButton
            />
          )}
          <div className="ddp-annotate__export">
            {(['json', 'csv', 'coco'] as const).map((format) => (
              <Button
                key={format}
                kind="tertiary"
                size="sm"
                disabled={annotations.length === 0 || exportBusy !== null}
                onClick={() => void onExport(format)}
              >
                {exportBusy === format ? '…' : format.toUpperCase()}
              </Button>
            ))}
          </div>
          <p className="ddp-annotate__hint">COCO n’exporte que les boîtes (détection d’objets).</p>

          <h2 className="ddp-annotate__subtitle">Images extraites ({frames.length})</h2>
          {frames.length === 0 ? (
            <p className="ddp-annotate__empty">Aucune image extraite pour l’instant.</p>
          ) : (
            <ul className="ddp-annotate__frames">
              {frames.map((frame) => (
                <li
                  key={frame.id}
                  className={`ddp-annotate__frame${
                    frame.id === selectedFrameId ? ' ddp-annotate__frame--active' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="ddp-annotate__frame-btn"
                    onClick={() => setSelectedFrameId(frame.id)}
                    title={frameOrigin(frame)}
                  >
                    <img
                      src={frame.thumbUrl ?? frame.dataUrl}
                      alt=""
                      className="ddp-annotate__frame-thumb"
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="ddp-annotate__frame-time">{formatMs(frame.timestampMs)}</span>
                    <span className="ddp-annotate__frame-count">
                      {boxCountAt(frame.timestampMs)} boîte(s)
                      {frame.serverId ? '' : ' · …'}
                    </span>
                  </button>
                  <Button kind="ghost" size="sm" onClick={() => void removeFrame(frame)}>
                    Retirer
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <h2 className="ddp-annotate__subtitle">
            Toutes les annotations ({visibleAnnotations.length})
          </h2>
          {labels.length > 0 && (
            <Select
              id="filter-label"
              labelText="Filtrer par label"
              value={filterLabelId}
              onChange={(e) => setFilterLabelId(e.target.value)}
            >
              <SelectItem value="" text="Tous les labels" />
              {labels.map((label) => (
                <SelectItem key={label.id} value={label.id} text={label.name} />
              ))}
            </Select>
          )}
          {visibleAnnotations.length === 0 ? (
            <p className="ddp-annotate__empty">
              {annotations.length === 0
                ? 'Aucune annotation enregistrée.'
                : 'Aucune annotation pour ce label.'}
            </p>
          ) : (
            <ul className="ddp-annotate__list">
              {visibleAnnotations.map((annotation) => (
                <li key={annotation.id} className="ddp-annotate__item">
                  <span className="ddp-annotate__seek">
                    <span
                      className="ddp-annotate__swatch"
                      style={{ backgroundColor: annotation.labelColor }}
                    />
                    <span className="ddp-annotate__item-label">{annotation.labelName}</span>
                    <span className="ddp-annotate__item-time">{formatMs(annotation.startMs)}</span>
                  </span>
                  <span className="ddp-annotate__item-actions">
                    <Button kind="ghost" size="sm" onClick={() => setEditing(annotation)}>
                      Éditer
                    </Button>
                    <Button
                      kind="ghost"
                      size="sm"
                      onClick={() => void onDeleteAnnotation(annotation.id)}
                    >
                      Supprimer
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      {editing && (
        <EditAnnotationModal
          annotation={editing}
          labels={labels}
          isSuggestion={editing.source === 'MODEL'}
          onClose={() => setEditing(null)}
          onSave={onSaveEdit}
        />
      )}

      {menu && selectedFrame && (
        <ul className="ddp-ctxmenu" style={{ top: menu.y, left: menu.x }} role="menu">
          <li>
            <button type="button" onClick={() => void downloadFrame(false)}>
              Télécharger l’image
            </button>
          </li>
          <li>
            <button type="button" onClick={() => void downloadFrame(true)}>
              Télécharger l’image annotée
            </button>
          </li>
          {selectedFrame.serverId && (
            <li>
              <button type="button" onClick={() => void onShareImage()}>
                Partager l’image
              </button>
            </li>
          )}
          {frameBoxes.length > 0 && (
            <li>
              <button type="button" onClick={() => setEditing(frameBoxes[0]!)}>
                Modifier l’annotation
              </button>
            </li>
          )}
          <li>
            <button
              type="button"
              className="ddp-ctxmenu__danger"
              onClick={() => void removeFrame(selectedFrame)}
            >
              Supprimer l’image
            </button>
          </li>
        </ul>
      )}
    </section>
  );
}

/**
 * Édition d'une annotation : changer son label ou sa note (FR-26).
 *
 * En mode `isSuggestion` (incrément 2), la même boîte de dialogue sert à **corriger puis valider**
 * une prédiction du modèle : on n'y ajuste que le label (la note n'appartient pas au flux de
 * validation) et le bouton principal valide la suggestion.
 */
function EditAnnotationModal({
  annotation,
  labels,
  isSuggestion = false,
  onClose,
  onSave,
}: {
  annotation: Annotation;
  labels: Label[];
  isSuggestion?: boolean;
  onClose: () => void;
  onSave: (annotationId: string, labelId: string, note: string) => Promise<void>;
}) {
  const [labelId, setLabelId] = useState(annotation.labelId);
  const [note, setNote] = useState(annotation.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(annotation.id, labelId, note);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      modalHeading={isSuggestion ? 'Corriger et valider la suggestion' : 'Éditer l’annotation'}
      primaryButtonText={
        busy
          ? isSuggestion
            ? 'Validation…'
            : 'Enregistrement…'
          : isSuggestion
            ? 'Valider'
            : 'Enregistrer'
      }
      secondaryButtonText="Annuler"
      primaryButtonDisabled={busy}
      onRequestClose={onClose}
      onRequestSubmit={() => void onSubmit()}
    >
      <div className="ddp-annotate__edit">
        <Select
          id="edit-label"
          labelText="Label"
          value={labelId}
          onChange={(e) => setLabelId(e.target.value)}
        >
          {labels.map((label) => (
            <SelectItem key={label.id} value={label.id} text={label.name} />
          ))}
        </Select>
        {!isSuggestion && (
          <TextArea
            id="edit-note"
            labelText="Note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}
        {error && (
          <InlineNotification
            kind="error"
            title="Échec"
            subtitle={error}
            lowContrast
            hideCloseButton
          />
        )}
      </div>
    </Modal>
  );
}

/**
 * Options de tracé distinguant une **suggestion du modèle** d'une annotation humaine (incrément 2) :
 * trait pointillé et opacité proportionnelle à la confiance — une prédiction incertaine s'affiche
 * en filigrane, une prédiction sûre presque comme une vraie boîte.
 */
interface DrawBoxStyle {
  dashed?: boolean;
  alpha?: number;
}

function drawBox(
  ctx: CanvasRenderingContext2D,
  box: NormalizedBox,
  width: number,
  height: number,
  color: string,
  labelText: string | null,
  emphasized: boolean,
  style?: DrawBoxStyle,
) {
  const dashed = style?.dashed ?? false;
  const alpha = style?.alpha ?? 1;
  const stroke = alpha < 1 ? withAlpha(color, alpha) : color;
  const x = box.x * width;
  const y = box.y * height;
  const w = box.width * width;
  const h = box.height * height;
  // La boîte survolée dans la liste est remplie et surlignée : on repère d'un coup d'œil
  // laquelle on s'apprête à supprimer.
  if (emphasized) {
    ctx.fillStyle = withAlpha(color, 0.25);
    ctx.fillRect(x, y, w, h);
  }
  ctx.save();
  if (dashed) ctx.setLineDash([6, 4]);
  ctx.lineWidth = emphasized ? 4 : 2;
  ctx.strokeStyle = stroke;
  ctx.strokeRect(x, y, w, h);
  ctx.restore();
  if (labelText) {
    ctx.font = '12px "IBM Plex Sans", sans-serif';
    const paddingX = 4;
    const textWidth = ctx.measureText(labelText).width;
    // Bandeau de label toujours lisible : on ne le laisse pas descendre sous ~0.6 d'opacité.
    ctx.fillStyle = alpha < 1 ? withAlpha(color, Math.max(alpha, 0.6)) : color;
    ctx.fillRect(x, y - 16, textWidth + paddingX * 2, 16);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(labelText, x + paddingX, y - 4);
  }
}

/** Opacité d'une suggestion selon sa confiance : jamais nulle (0.4) pour rester visible, jusqu'à 1. */
function confidenceAlpha(confidence: number | null): number {
  return 0.4 + 0.6 * (confidence ?? 0.5);
}

/** Rend l'image (à sa résolution native) éventuellement surchargée de ses boîtes, en blob PNG. */
async function renderFrameBlob(src: string, boxes: Annotation[]): Promise<Blob> {
  const image = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas');
  ctx.drawImage(image, 0, 0);
  for (const annotation of boxes) {
    if (annotation.box) {
      drawBox(
        ctx,
        annotation.box,
        canvas.width,
        canvas.height,
        annotation.labelColor,
        annotation.labelName,
        false,
      );
    }
  }
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('blob'))), 'image/png');
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    // Nécessaire pour dessiner une image d'une autre origine (URL pré-signée) sans « teinter » le canvas.
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('image'));
    image.src = src;
  });
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function toBox(a: Point, b: Point): NormalizedBox {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/** Traduit une couleur hex `#rrggbb` en `rgba(...)` pour un remplissage semi-transparent. */
function withAlpha(hex: string, alpha: number): string {
  const hex6 = /^#([0-9a-f]{6})$/i.exec(hex)?.[1];
  if (!hex6) return hex;
  const int = parseInt(hex6, 16);
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
}

function formatMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const millis = Math.floor(ms % 1000);
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

function formatDate(value: Date | string): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/** Provenance d'une image extraite, en infobulle : qui l'a extraite et quand (FR-26). */
function frameOrigin(frame: ExtractedFrame): string {
  if (!frame.createdAt) return 'Extraction en cours…';
  const when = `extraite le ${formatDate(frame.createdAt)}`;
  return frame.createdByName ? `${when} par ${frame.createdByName}` : when;
}

/**
 * Vignette JPEG réduite d'une frame (bande latérale, listes) : on redimensionne la plus grande
 * dimension à `maxDim` en conservant le ratio. Le plein format reste le PNG ; ceci n'en est qu'un
 * aperçu léger (quelques dizaines de Ko contre plusieurs Mo).
 */
async function thumbnailBlob(source: HTMLCanvasElement, maxDim = 400): Promise<Blob | null> {
  const scale = Math.min(1, maxDim / Math.max(source.width, source.height));
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, width, height);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.72));
}

/** Provenance d'une catégorie, en infobulle : qui l'a créée et quand (FR-27). */
function labelOrigin(label: Label): string {
  const when = `créée le ${formatDate(label.createdAt)}`;
  return label.createdByName
    ? `${label.name} — ${when} par ${label.createdByName}`
    : `${label.name} — ${when}`;
}
